/**
 * 产品对比功能
 * 管理产品对比列表的添加、删除和显示
 */
class ProductCompare {
  constructor() {
    this.storageKey = 'product_compare_list';
    this.maxProducts = 4; // 最多对比4个产品
    this.init();
  }

  init() {
    // 初始化按钮事件
    this.bindEvents();
    // 更新所有按钮状态
    this.updateAllButtons();
    // 更新浮动按钮
    this.updateFloatingButton();
  }

  bindEvents() {
    // 委托事件监听
    document.addEventListener('click', (e) => {
      const button = e.target.closest('[data-compare-action]');
      if (button) {
        e.preventDefault();
        const action = button.dataset.compareAction;
        const productId = button.dataset.productId;
        const productHandle = button.dataset.productHandle;
        const productTitle = button.dataset.productTitle;
        const productImage = button.dataset.productImage;
        const productPrice = button.dataset.productPrice;
        const productCompareAtPrice = button.dataset.productCompareAtPrice;
        const productMetafields = button.dataset.productMetafields;

        if (action === 'add') {
          this.addProduct({
            id: productId,
            handle: productHandle,
            title: productTitle,
            image: productImage,
            price: productPrice,
            compare_at_price: productCompareAtPrice || null
          }, button);
        } else if (action === 'remove') {
          this.removeProduct(productId);
        } else if (action === 'clear') {
          this.clearAll();
        }
      }
    });
  }

  // 获取对比列表
  getCompareList() {
    try {
      const list = localStorage.getItem(this.storageKey);
      return list ? JSON.parse(list) : [];
    } catch (e) {
      console.error('读取对比列表失败:', e);
      return [];
    }
  }

  // 保存对比列表
  saveCompareList(list) {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(list));
      this.updateAllButtons();
      this.updateFloatingButton();
      // 触发自定义事件
      document.dispatchEvent(new CustomEvent('compare:updated', {
        detail: { count: list.length }
      }));
    } catch (e) {
      console.error('保存对比列表失败:', e);
    }
  }

  // 添加产品到对比列表
  async addProduct(product, button) {
    const list = this.getCompareList();
    
    // 检查是否已存在
    if (list.find(p => p.id === product.id)) {
      this.showNotification('This product is already in the comparison list', 'info');
      return;
    }

    // 检查是否达到最大数量
    if (list.length >= this.maxProducts) {
      this.showNotification(`You can compare up to ${this.maxProducts} products`, 'warning');
      return;
    }

    // 直接从传入的按钮获取元字段数据
    if (button && button.dataset.productMetafields) {
      try {
        const metafieldsStr = button.dataset.productMetafields;
        console.log('📦 元字段数据:', metafieldsStr);
        
        const metafields = JSON.parse(metafieldsStr);
        console.log('✅ 解析成功:', metafields);
        
        // 只有当 metafields 不是空对象时才添加
        if (Object.keys(metafields).length > 0) {
          product.metafields = metafields;
        }
      } catch (e) {
        console.error('❌ JSON 解析失败:', e);
        console.error('原始数据:', button.dataset.productMetafields);
      }
    }

    list.push(product);
    this.saveCompareList(list);
    
    console.log('💾 保存的产品:', product);
    console.log('💾 完整列表:', list);
    
    this.showNotification('Added to comparison list', 'success');
  }

  // 从对比列表移除产品
  removeProduct(productId) {
    const list = this.getCompareList();
    const newList = list.filter(p => p.id !== productId);
    this.saveCompareList(newList);
    this.showNotification('Removed from comparison list', 'success');
    
    // 如果在对比页面，重新加载
    if (window.location.pathname.includes('/pages/compare')) {
      location.reload();
    }
  }

  // 清空对比列表
  clearAll() {
    if (confirm('Are you sure you want to clear all comparison products?')) {
      this.saveCompareList([]);
      this.showNotification('Comparison list cleared', 'success');
      
      // 如果在对比页面，重新加载
      if (window.location.pathname.includes('/pages/compare')) {
        location.reload();
      }
    }
  }

  // 检查产品是否在对比列表中
  isInCompareList(productId) {
    const list = this.getCompareList();
    return list.some(p => p.id === productId);
  }

  // 更新所有按钮状态
  updateAllButtons() {
    const list = this.getCompareList();
    const currentCount = list.length;
    
    const buttons = document.querySelectorAll('[data-compare-action]');
    buttons.forEach(button => {
      const productId = button.dataset.productId;
      
      // 只更新有 productId 的按钮(产品对比按钮)
      // 跳过 clear、其他功能按钮
      if (!productId) return;
      
      if (this.isInCompareList(productId)) {
        button.classList.add('is-active');
        button.setAttribute('data-compare-action', 'remove');
        
        // 同步复选框状态
        const checkbox = button.querySelector('input[type="checkbox"]');
        if (checkbox) checkbox.checked = true;

        const textElement = button.querySelector('[data-compare-text]');
        if (textElement) {
          // 改为显示 Remove Compare
          textElement.textContent = button.dataset.removeText || 'Remove Compare';
        }
      } else {
        button.classList.remove('is-active');
        button.setAttribute('data-compare-action', 'add');

        // 同步复选框状态
        const checkbox = button.querySelector('input[type="checkbox"]');
        if (checkbox) checkbox.checked = false;

        const textElement = button.querySelector('[data-compare-text]');
        if (textElement) {
          textElement.textContent = button.dataset.addText || 'Add to Compare';
        }
      }
    });
  }

  // 更新浮动按钮
  updateFloatingButton() {
    const list = this.getCompareList();
    
    // 支持旧版浮动按钮 ID
    const floatingButton = document.getElementById('compare-floating-button');
    if (floatingButton) {
      const countElement = floatingButton.querySelector('[data-compare-count]');
      if (countElement) {
        countElement.textContent = list.length;
      }

      if (list.length > 0) {
        floatingButton.style.display = 'flex';
      } else {
        floatingButton.style.display = 'none';
      }
    }
    
    // 支持新版浮动栏 ID
    const floatingBar = document.getElementById('compare-floating-bar');
    if (floatingBar) {
      if (list.length > 0) {
        floatingBar.classList.add('active');
      } else {
        floatingBar.classList.remove('active');
      }
    }
  }

  // 显示通知
  showNotification(message, type = 'info') {
    // 创建通知元素
    const notification = document.createElement('div');
    notification.className = `compare-notification compare-notification--${type}`;
    notification.textContent = message;
    
    // 添加到页面
    document.body.appendChild(notification);
    
    // 显示动画
    setTimeout(() => {
      notification.classList.add('is-visible');
    }, 10);
    
    // 3秒后移除
    setTimeout(() => {
      notification.classList.remove('is-visible');
      setTimeout(() => {
        notification.remove();
      }, 300);
    }, 3000);
  }
}

// 初始化
if (typeof window !== 'undefined') {
  window.productCompare = new ProductCompare();
  
  // 在对比页面加载产品数据
  if (window.location.pathname.includes('/pages/compare')) {
    document.addEventListener('DOMContentLoaded', () => {
      window.productCompare.updateFloatingButton();
    });
  }
}

